import React, { PureComponent } from 'react';
import T from 'prop-types';
// redux
import { compose } from 'redux';
import { connect } from 'react-redux/';
import { updateRecoveryPlan } from '../../../redux/reducers/recovery';
// hoc
import { withFormik } from 'formik';
// components
import Switch from 'rc-switch';
import Preloader from '../../../components/Preloader/Preloader';
import Icon from '../../../components/Icon/Icon';
import Input from '../../../components/Input/Input';
// utils
import * as yup from 'yup';
import { toastResponseErrors } from '../../../utils/response';
// styles
import styles from './PersonalValues.css';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);

class PersonalValues extends PureComponent {
  state = {
    editMode: false,
    editingIndex: null
  };

  toggleEditMode = () => {
    const { resetForm } = this.props;

    this.setState(
      ({ editMode }) => ({
        editMode: !editMode,
        editingIndex: null
      }),
      () => {
        const { editMode } = this.state;
        if (!editMode) resetForm();
      }
    );
  };

  editItemOn = index => this.setState({ editingIndex: index });

  editItemOff = () => {
    const {
      setFieldValue,
      values: { data }
    } = this.props;

    setFieldValue('data', data.filter(value => value));
    this.setState({ editingIndex: null });
  };

  deleteItem = (event, index) => {
    const {
      setFieldValue,
      values: { data },
      handleSubmit
    } = this.props;

    this.editItemOff();
    const newData = [...data];
    newData.splice(index, 1);
    setFieldValue('data', newData.filter(value => value));
    handleSubmit(event);
  };

  resetItem = (event, index) => {
    const {
      setFieldValue,
      values: { data },
      initialValues,
      handleSubmit
    } = this.props;
    const value = data[index];
    const initialValue = initialValues.data[index];
    const newData = [...data];

    this.editItemOff();

    if (initialValue === value) return;

    if (initialValue) {
      newData[index] = initialValue;
      setFieldValue('data', newData.filter(value => value));
    } else {
      newData.splice(index, 1);
      setFieldValue('data', newData.filter(value => value));
    }
    handleSubmit(event);
  };

  addNewItem = () => {
    const {
      setFieldValue,
      values: { data }
    } = this.props;
    const newData = [...data, ''];
    setFieldValue('data', newData);

    this.setState({
      editingIndex: data.length,
      editMode: true
    });
  };

  updateData = event => {
    const { handleSubmit } = this.props;
    this.editItemOff();
    handleSubmit(event);
  };

  handleValueChange = event => {
    const { editingIndex } = this.state;
    const {
      setFieldValue,
      values: { data }
    } = this.props;
    const changedData = [...data];
    changedData[editingIndex] = event.target.value;
    setFieldValue('data', changedData);
  };

  renderActionButtons = index => {
    const { userColor, isSubmitting } = this.props;
    const buttonStyle = { backgroundColor: userColor };

    return (
      <div className={cx('action-buttons')}>
        <button
          className={cx('action-button', 'plain', 'hint--top', {
            disabled: isSubmitting
          })}
          disabled={isSubmitting}
          onClick={() => this.editItemOn(index)}
          data-hint="Edit"
        >
          <Icon type="pen" className={cx('icon')} style={buttonStyle} />
        </button>
        <button
          className={cx('action-button', 'plain', 'hint--top', {
            disabled: isSubmitting
          })}
          disabled={isSubmitting}
          onClick={event => this.deleteItem(event, index)}
          data-hint="Delete"
        >
          <Icon type="delete" className={cx('icon')} style={buttonStyle} />
        </button>
      </div>
    );
  };

  renderEditingButtons = index => {
    const { isSubmitting } = this.props;
    return (
      <div className={cx('action-buttons')}>
        <button
          className={cx('action-button', 'save', 'hint--top', {
            disabled: isSubmitting
          })}
          disabled={isSubmitting}
          onClick={this.updateData}
          data-hint="Update"
        >
          <Icon type="check" className={cx('action-icon')} />
        </button>
        <button
          className={cx('action-button', 'cancel', 'hint--top', {
            disabled: isSubmitting
          })}
          disabled={isSubmitting}
          onClick={event => this.resetItem(event, index)}
          data-hint="Reset"
        >
          <Icon type="close" className={cx('action-icon')} />
        </button>
      </div>
    );
  };

  render() {
    const {
      values: { data },
      isSubmitting,
      userColor,
      title
    } = this.props;
    const { editMode, editingIndex } = this.state;

    const switchStyle = editMode
      ? {
          backgroundColor: userColor,
          borderColor: userColor
        }
      : null;

    const canAdd =
      !isSubmitting && data.every(element => element) && editingIndex === null;

    return (
      <div>
        <div className={cx('title-row')}>
          <Preloader custom isActive={isSubmitting} />
          <h4>{title}</h4>
          <div className={cx('edit-mode-toggle')}>
            <span className="mr-5">Edit</span>
            <Switch
              checked={editMode}
              onChange={this.toggleEditMode}
              className={cx('switcher')}
              style={switchStyle}
            />
          </div>
        </div>
        <ul>
          {data.map((value, index) => {
            const isEditing = editingIndex === index;
            const buttons = isEditing
              ? this.renderEditingButtons(index)
              : this.renderActionButtons(index);

            return isEditing ? (
              <div key={index} className={cx('edit-container')}>
                <Input
                  autoFocus
                  color={userColor}
                  name={`item-${index}`}
                  className={cx('data-input')}
                  wrapperClassName={cx('input-wrapper')}
                  placeholder="Type a personal value"
                  value={data[index]}
                  disabled={isSubmitting}
                  onChange={this.handleValueChange}
                />
                {buttons}
              </div>
            ) : (
              <div key={index} className={cx('item-wrapper')}>
                <li>{value}</li> {editMode && buttons}
              </div>
            );
          })}
        </ul>
        {canAdd && (
          <button
            className={cx('add-item')}
            onClick={this.addNewItem}
            style={{ color: userColor }}
          >
            <Icon
              type="add"
              className={cx('add-item-icon')}
              style={{ backgroundColor: userColor }}
            />
            Add new
          </button>
        )}
      </div>
    );
  }
}

PersonalValues.propTypes = {
  values: T.shape({
    data: T.arrayOf(T.string)
  }).isRequired,
  isSubmitting: T.bool.isRequired,
  customTriggers: T.arrayOf(T.string),
  handleSubmit: T.func.isRequired,
  setFieldValue: T.func.isRequired,
  title: T.string.isRequired,
  userColor: T.string,
  field: T.string.isRequired
};

const formikHOC = withFormik({
  enableReinitialize: true,
  validateOnChange: false,
  mapPropsToValues: ({ data = [] }) => ({
    data: data.content_progress.answers_list || []
  }),
  validationSchema: yup.object().shape({
    data: yup
      .array()
      .compact()
      .min(1, 'At least one value is required')
      .required('Field is required')
  }),
  handleSubmit: (
    values,
    { setSubmitting, props: { data, field, updateRecoveryPlan } }
  ) => {
    setSubmitting(true);

    updateRecoveryPlan({
      field,
      data: {
        id: data.input_item_id,
        question: {
          id: data.content_progress.question.id,
          answers_list: values.data.filter(value => value)
        }
      }
    })
      .then(() => {
        setSubmitting(false);
      })
      .catch(error => {
        setSubmitting(false);
        toastResponseErrors(error);
      });
  }
});

const enhance = compose(
  connect(
    null,
    { updateRecoveryPlan }
  ),
  formikHOC
);

export default enhance(PersonalValues);



// WEBPACK FOOTER //
// ./src/containers/RecoveryPlan/components/PersonalValues.jsx